export type FilterHierarchyNode = {
  id: string;
  name: string;
  description?: string;
  options?: FilterHierarchyNode[];
};

export type FilterHierarchyLevel = {
  id: string;
  name: string;
};

export type FilterHierarchyConfig = {
  filterHierarchy: FilterHierarchyLevel[];
  options: FilterHierarchyNode[];
  /** Optional preferred defaults; missing levels fall back to the first valid option. */
  defaultSelection?: Record<string, string>;
};

const FILTER_HIERARCHY_TYPE_PATTERN = /filter[\s_-]*hierarchy/i;

export const isFilterHierarchyType = (type: unknown): boolean =>
  typeof type === 'string' && FILTER_HIERARCHY_TYPE_PATTERN.test(type.trim());

const normalizeLevels = (raw: unknown): FilterHierarchyLevel[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((level: any) => {
      const id = level?.id ?? level?.name;
      const name = level?.name ?? level?.id;
      if (id === undefined || id === null || name === undefined || name === null) return null;
      return { id: String(id), name: String(name) };
    })
    .filter((level): level is FilterHierarchyLevel => level !== null);
};

const readNodeDescription = (node: any): string | undefined => {
  const raw = node?.description ?? node?.Description;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
};

const normalizeNodes = (raw: unknown): FilterHierarchyNode[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((node: any) => {
      const id = node?.id ?? node?.name;
      const name = node?.name ?? node?.id;
      if (id === undefined || id === null || name === undefined || name === null) return null;
      const children = normalizeNodes(node?.options ?? node?.children ?? node?.items);
      const description = readNodeDescription(node);
      return {
        id: String(id),
        name: String(name),
        ...(description ? { description } : {}),
        ...(children.length ? { options: children } : {}),
      };
    })
    .filter((node): node is FilterHierarchyNode => node !== null);
};

const normalizeDefaultSelection = (raw: unknown): Record<string, string> | undefined => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const entries = Object.entries(raw as Record<string, unknown>)
    .map(([key, value]) => {
      if (value === undefined || value === null || value === '') return null;
      return [String(key), String(value)] as const;
    })
    .filter((entry): entry is readonly [string, string] => entry !== null);
  if (!entries.length) return undefined;
  return Object.fromEntries(entries);
};

/** Normalize API / legacy payloads into a shared cascading filter config. */
export function normalizeFilterHierarchyConfig(raw: unknown): FilterHierarchyConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;

  const filterHierarchy = normalizeLevels(
    source.hierarchy ??
      source.filterHierarchy ??
      source['filter-hierarchy'] ??
      source.levels ??
      source.Levels ??
      source.filter_hierarchy,
  );
  const options = normalizeNodes(
    source.options ?? source.children ?? source.items ?? source.tree ?? source.Nodes ?? source.nodes,
  );
  if (!filterHierarchy.length || !options.length) return null;

  const defaultSelection = normalizeDefaultSelection(
    source.defaultSelection ?? source['default-selection'] ?? source.defaultValue ?? source['default-value'],
  );

  return {
    filterHierarchy,
    options,
    ...(defaultSelection ? { defaultSelection } : {}),
  };
}

/** Extract the first Filter Hierarchy filter from a nav filters list or single object. */
export function extractFilterHierarchyFromFilters(filters: unknown): FilterHierarchyConfig | null {
  if (!filters) return null;

  if (!Array.isArray(filters) && typeof filters === 'object') {
    const asSingle = normalizeFilterHierarchyConfig(filters);
    if (asSingle) return asSingle;
  }

  const list = Array.isArray(filters)
    ? filters
    : typeof filters === 'object' && Array.isArray((filters as { items?: unknown }).items)
      ? ((filters as { items: unknown[] }).items as unknown[])
      : [];

  for (const filter of list) {
    if (!filter || typeof filter !== 'object') continue;
    const candidate = filter as {
      type?: unknown;
      hierarchy?: unknown;
      filterHierarchy?: unknown;
    };
    if (!isFilterHierarchyType(candidate.type) && !candidate.hierarchy && !candidate.filterHierarchy) {
      continue;
    }
    const config = normalizeFilterHierarchyConfig(filter);
    if (config) return config;
  }
  return null;
}

export function omitFilterHierarchyFilters<
  T extends { type?: unknown; hierarchy?: unknown; filterHierarchy?: unknown },
>(filters: T[] | undefined): T[] | undefined {
  if (!Array.isArray(filters)) return filters;
  return filters.filter(
    filter => !isFilterHierarchyType(filter?.type) && !filter?.hierarchy && !filter?.filterHierarchy,
  );
}
